import { useId, useState } from 'react';
import { Column } from '@tanstack/react-table';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  ColumnFilterConfig,
  ColumnFilterOption,
  ColumnFilterValue,
  isFilterValueActive,
  MAX_TIME_FILTER_DAYS,
} from '@/lib/table-query';
import { nextDateOnlyDay } from '@/lib/utils/custom-date/custom-date';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { TimeInput } from '@/components/core/time-input/time-input';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';

export const emptyValue = (config: ColumnFilterConfig): ColumnFilterValue => {
  switch (config.type) {
    case 'text':
      return { type: 'text', value: '' };
    case 'options':
      return { type: 'options', values: [] };
    case 'number':
      return { type: 'number' };
    case 'time':
      return { type: 'time' };
    default:
      return { type: config.type };
  }
};

/** The last day a server-side time filter may reach from `fromDay`. */
const lastTimeFilterDay = (fromDay: string): string => {
  let day = fromDay;
  for (let i = 1; i < MAX_TIME_FILTER_DAYS; i++) day = nextDateOnlyDay(day);
  return day;
};

/** A server-side time filter needs its day range before it can be applied. */
export const isMissingDays = (config: ColumnFilterConfig, draft: ColumnFilterValue): boolean =>
  config.type === 'time' &&
  Boolean(config.withDays) &&
  draft.type === 'time' &&
  isFilterValueActive(draft) &&
  !(draft.fromDay && draft.toDay);

const toNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * A time filter's bound typed as 24-hour "HH:mm" (`TimeInput`). The filter gets the time only once it
 * is a valid one; until then (or when cleared) the bound is unset.
 */
const TimeFilterField = ({
  value,
  onChange,
}: Readonly<{ value?: string; onChange: (value: string | undefined) => void }>) => {
  const [text, setText] = useState(value ?? '');
  const change = (next: string) => {
    setText(next);
    onChange(TIME_PATTERN.test(next) ? next : undefined);
  };
  // A complete time that no longer matches the filter was changed from outside (e.g. Clear).
  const shown = TIME_PATTERN.test(text) && text !== value ? (value ?? '') : text;
  return <TimeInput value={shown} onChange={change} />;
};

interface FilterEditorProps {
  config: ColumnFilterConfig;
  draft: ColumnFilterValue;
  onDraftChange: (draft: ColumnFilterValue) => void;
  onSubmit: () => void;
  /**
   * The roomier layout of the cards' filter dialog: options side by side, and each date picker
   * beside its "From"/"To" label. The header popover keeps them stacked.
   */
  inline?: boolean;
}

/** A date picker under its label; the `inline` layout puts two of them side by side. */
const InlineDateRow = ({
  label,
  ...props
}: { label: string } & React.ComponentProps<typeof DatePicker>) => (
  <div className="flex min-w-0 flex-col gap-1">
    <Label className="text-xs">{label}</Label>
    <DatePicker {...props} />
  </div>
);

export const FilterEditor = ({
  config,
  draft,
  onDraftChange,
  onSubmit,
  inline = false,
}: Readonly<FilterEditorProps>) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const dateGroupClass = inline ? 'grid grid-cols-2 gap-3' : 'flex flex-col gap-2';

  if (draft.type === 'text') {
    return (
      <Input
        autoFocus
        value={draft.value}
        placeholder={t('SEARCH')}
        onChange={(event) => onDraftChange({ type: 'text', value: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSubmit();
        }}
      />
    );
  }

  if (draft.type === 'options' && config.type === 'options' && config.dropdown) {
    // One pick from a searchable dropdown (a long list); "All" clears the filter.
    const picked = draft.values[0];
    return (
      <SearchableSelect
        value={picked === undefined ? '' : String(picked)}
        options={[
          { value: '', label: t('ALL') },
          ...config.options.map((option) => ({
            value: String(option.value),
            label: option.label,
          })),
        ]}
        onChange={(value) => {
          const option = config.options.find((candidate) => String(candidate.value) === value);
          onDraftChange({ type: 'options', values: option ? [option.value] : [] });
        }}
        placeholder={t('ALL')}
        searchPlaceholder={t('SEARCH')}
        emptyText={t('NO_RESULTS_FOUND')}
        className="w-full"
      />
    );
  }

  if (draft.type === 'options' && config.type === 'options') {
    const toggle = (option: ColumnFilterOption, checked: boolean) =>
      onDraftChange({
        type: 'options',
        values: checked
          ? [...draft.values, option.value]
          : draft.values.filter((value) => value !== option.value),
      });
    return (
      <div
        className={
          inline
            ? 'flex flex-wrap gap-x-4 gap-y-2'
            : 'flex max-h-60 flex-col gap-2 overflow-y-auto overscroll-contain'
        }
      >
        {config.options.map((option) => {
          const id = `${idPrefix}-${String(option.value)}`;
          return (
            <div key={String(option.value)} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={draft.values.includes(option.value)}
                onCheckedChange={(checked) => toggle(option, checked === true)}
              />
              <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                {option.label}
              </Label>
            </div>
          );
        })}
      </div>
    );
  }

  if (draft.type === 'date' || draft.type === 'datetime') {
    return (
      <div className={dateGroupClass}>
        <InlineDateRow
          label={t('FROM')}
          value={draft.from ?? ''}
          max={draft.to || undefined}
          onChange={(from) => onDraftChange({ ...draft, from: from || undefined })}
        />
        <InlineDateRow
          label={t('TO')}
          value={draft.to ?? ''}
          min={draft.from || undefined}
          onChange={(to) => onDraftChange({ ...draft, to: to || undefined })}
        />
      </div>
    );
  }

  if (draft.type === 'time' && config.type === 'time') {
    return (
      <div className="flex flex-col gap-2">
        {config.withDays && (
          <div className={dateGroupClass}>
            <InlineDateRow
              label={t('START_DATE')}
              value={draft.fromDay ?? ''}
              max={draft.toDay || undefined}
              onChange={(fromDay) => onDraftChange({ ...draft, fromDay: fromDay || undefined })}
            />
            <InlineDateRow
              label={t('END_DATE')}
              value={draft.toDay ?? ''}
              min={draft.fromDay || undefined}
              max={draft.fromDay ? lastTimeFilterDay(draft.fromDay) : undefined}
              onChange={(toDay) => onDraftChange({ ...draft, toDay: toDay || undefined })}
            />
          </div>
        )}
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Label className="text-xs">{t('FROM')}</Label>
            <TimeFilterField
              value={draft.from}
              onChange={(from) => onDraftChange({ ...draft, from })}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label className="text-xs">{t('TO')}</Label>
            <TimeFilterField value={draft.to} onChange={(to) => onDraftChange({ ...draft, to })} />
          </div>
        </div>
        {isMissingDays(config, draft) && (
          <span className="text-xs text-muted-foreground">
            {t('TIME_FILTER_DAYS_REQUIRED', { count: MAX_TIME_FILTER_DAYS })}
          </span>
        )}
      </div>
    );
  }

  if (draft.type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={draft.min ?? ''}
          placeholder={t('MIN')}
          onChange={(event) => onDraftChange({ ...draft, min: toNumber(event.target.value) })}
        />
        <Input
          type="number"
          value={draft.max ?? ''}
          placeholder={t('MAX')}
          onChange={(event) => onDraftChange({ ...draft, max: toNumber(event.target.value) })}
        />
      </div>
    );
  }

  return null;
};

interface DataTableColumnFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
  config: ColumnFilterConfig;
  title: string;
}

/**
 * The filter icon of a column header. Opens a popover with the editor `meta.filter` asks for and
 * applies it to the column's filter value on "Apply" (or Enter), so a server-side list queries once
 * per change rather than per keystroke. The icon is highlighted while a filter is applied.
 */
export const DataTableColumnFilter = <TData, TValue>({
  column,
  config,
  title,
}: Readonly<DataTableColumnFilterProps<TData, TValue>>) => {
  const { t } = useTranslation();
  const applied = column.getFilterValue() as ColumnFilterValue | undefined;
  const isActive = isFilterValueActive(applied);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ColumnFilterValue>(() => applied ?? emptyValue(config));

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(applied ?? emptyValue(config));
    setOpen(next);
  };

  const apply = () => {
    if (isMissingDays(config, draft)) return;
    column.setFilterValue(isFilterValueActive(draft) ? draft : undefined);
    setOpen(false);
  };

  const clear = () => {
    column.setFilterValue(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-7 w-7', isActive ? 'text-primary' : 'text-muted-foreground')}
          aria-label={`${t('FILTER')} ${title}`}
        >
          <ListFilter className={cn('h-4 w-4', isActive && 'stroke-[2.5]')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-64 flex-col gap-3 p-3">
        <span className="text-sm font-medium">{title}</span>
        <FilterEditor config={config} draft={draft} onDraftChange={setDraft} onSubmit={apply} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={clear} disabled={!isActive}>
            {t('CLEAR')}
          </Button>
          <Button size="sm" onClick={apply} disabled={isMissingDays(config, draft)}>
            {t('APPLY')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
